import { useNavigate } from '@tanstack/react-router';
import type { CityDto, EventCategoryDto, EventStatus } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@upme/ui/components/select';
import { useEffect, useState } from 'react';
import { STATUS_LABEL } from '../lib/format';

export interface EventsFiltersState {
  cityId?: string | undefined;
  status?: EventStatus | undefined;
  categoryId?: string | undefined;
  search?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
}

interface Props {
  cities: ReadonlyArray<CityDto>;
  categories: ReadonlyArray<EventCategoryDto>;
  value: EventsFiltersState;
}

const ANY = '__any__';

export function EventsFilters({ cities, categories, value }: Props) {
  const navigate = useNavigate();
  const [local, setLocal] = useState<EventsFiltersState>(value);

  // Sync from URL when user navigates back/forward.
  useEffect(() => {
    setLocal(value);
  }, [value]);

  const apply = (patch: Partial<EventsFiltersState>) => {
    const next = { ...local, ...patch };
    setLocal(next);
    navigate({
      to: '/events',
      search: () => {
        // Cursor resets on filter change.
        const out: Record<string, string> = {};
        for (const [k, v] of Object.entries(next)) {
          if (v !== undefined && v !== '' && v !== null) out[k] = String(v);
        }
        return out;
      },
    });
  };

  const reset = () => {
    setLocal({});
    navigate({ to: '/events', search: () => ({}) });
  };

  const hasAny = Object.values(local).some((v) => v !== undefined && v !== '');

  return (
    <div className="border rounded-md p-4 mb-4 grid gap-4 grid-cols-1 md:grid-cols-3 lg:grid-cols-6">
      <div>
        <Label className="text-xs">Город</Label>
        <Select
          value={local.cityId ?? ANY}
          onValueChange={(v) => apply({ cityId: v === ANY ? undefined : v })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Все" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Все</SelectItem>
            {cities.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs">Статус</Label>
        <Select
          value={local.status ?? ANY}
          onValueChange={(v) => apply({ status: v === ANY ? undefined : (v as EventStatus) })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Все" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Все</SelectItem>
            {(['DRAFT', 'PENDING_MODERATION', 'PUBLISHED', 'ARCHIVED'] as EventStatus[]).map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABEL[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs">Категория</Label>
        <Select
          value={local.categoryId ?? ANY}
          onValueChange={(v) => apply({ categoryId: v === ANY ? undefined : v })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Все" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Все</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.icon ? `${c.icon} ${c.name}` : c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="from" className="text-xs">
          С
        </Label>
        <Input
          id="from"
          type="date"
          value={local.from ?? ''}
          onChange={(e) => setLocal({ ...local, from: e.target.value || undefined })}
          onBlur={() => apply({ from: local.from })}
        />
      </div>

      <div>
        <Label htmlFor="to" className="text-xs">
          По
        </Label>
        <Input
          id="to"
          type="date"
          value={local.to ?? ''}
          onChange={(e) => setLocal({ ...local, to: e.target.value || undefined })}
          onBlur={() => apply({ to: local.to })}
        />
      </div>

      <div>
        <Label htmlFor="search" className="text-xs">
          Поиск по названию
        </Label>
        <Input
          id="search"
          value={local.search ?? ''}
          placeholder="например: вечеринка"
          onChange={(e) => setLocal({ ...local, search: e.target.value || undefined })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') apply({ search: local.search });
          }}
          onBlur={() => apply({ search: local.search })}
        />
      </div>

      {hasAny && (
        <div className="md:col-span-3 lg:col-span-6">
          <Button variant="ghost" size="sm" onClick={reset}>
            Сбросить фильтры
          </Button>
        </div>
      )}
    </div>
  );
}
